import { coerceToDate } from '@/utils/datetimes/javascriptDates';

type ValueOrArrayOrNull<T> = T | (T | null)[] | null;

/**
 * Defines a parser for a GraphQL field that we can wire into our cache type policies
 * to transform the raw GraphQL output into the type we want to use.
 *
 * While any individual field is defined as either a single non-nullable type, single nullable
 * type, a nullable array field, an array of non-nullable types, or an array of nullable types,
 * a parser defined using this class will handle any of those cases.
 *
 * Ex: the dateParser defined below can be used to parse a non-nullable date string, nullable date string,
 * a nullable array of date strings, an array of nullable date strings, or an array of non-nullable
 * date strings.
 */
class FieldParser<InputT, OutputT> {
    private readonly parser: (value: InputT) => OutputT;

    constructor(parser: (value: InputT) => OutputT) {
        this.parser = parser;
    }

    parse(value: ValueOrArrayOrNull<InputT>): ValueOrArrayOrNull<OutputT> {
        if (value === null) {
            return null;
        }
        if (Array.isArray(value)) {
            return this._parseArray(value);
        }
        return this._parseValue(value);
    }

    _parseValue(value: InputT | null): OutputT | null {
        if (value === null) {
            return null;
        }
        return this.parser(value);
    }

    _parseArray(value: (InputT | null)[]): (OutputT | null)[] {
        return value.map((v) => this._parseValue(v));
    }
}
const datetimeParser = new FieldParser<string, Date>((value) => new Date(value));
const dateParser = new FieldParser<string, Date>((value) => coerceToDate(value));

export const FieldPolicies = {
    date: {
        read: (value: ValueOrArrayOrNull<string>) => {
            if (value instanceof Date) {
                return value;
            }
            return dateParser.parse(value);
        },
    },
    datetime: {
        read: (value: ValueOrArrayOrNull<string>) => {
            if (value instanceof Date) {
                return value;
            }
            return datetimeParser.parse(value);
        },
    },
};
